import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apiService, { auth } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    apiService.clearAuthToken();
    setUser(null);
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || !apiService.isAuthenticated()) {
      apiService.clearAuthToken();
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      apiService.setAuthToken(token);
      const profile = await auth.getProfile();
      setUser(profile);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async credentials => {
    const data = await auth.login(credentials);
    apiService.setAuthToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const signup = async userData => auth.signup(userData);

  const updateProfile = async profileData => {
    const updated = await auth.updateProfile(profileData);
    setUser(updated);
    return updated;
  };

  const value = {
    user,
    loading,
    isAuthenticated: Boolean(user),
    isAdmin: user?.role === 'admin',
    login,
    signup,
    logout,
    updateProfile,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
